import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { apiClient, tokenStorage } from '../../../utils/api-client';
import { ZONE_EXPOSURES, type ZoneExposure } from '../../../types/zone';
import {
  DEFAULT_ROUTE_CORRIDOR,
  MAX_ROUTE_CORRIDOR,
  MAX_ROUTE_THICKNESS,
  MIN_ROUTE_CORRIDOR,
  MIN_ROUTE_THICKNESS,
} from '../../../components/climbs/route-line';
import type { CragRouteSector } from '../../../types/crag-route';

export interface SectorSettings {
  thickness: number;
  corridor: number;
  /** A compass point, or '' while nobody has said which way this wall faces. */
  exposure: ZoneExposure | '';
}

/** Radix Select treats '' as "no value", so "not recorded" carries a token. */
const NO_EXPOSURE = '__none__';

interface SectorSettingsPanelProps {
  sector: CragRouteSector;
  settings: SectorSettings;
  onSettingsChange: (patch: Partial<SectorSettings>) => void;
  onSave: () => void;
  /** A freshly uploaded photo for the sector, saved along with the settings. */
  onImageUploaded: (signedId: string, previewUrl: string) => void;
  pendingImageUrl: string | null;
  saving: boolean;
  error?: string | null;
}

/**
 * What is true of the sector rather than of any one route: the photo its lines
 * are drawn on, and how they are drawn.
 *
 * The style lives here because it is a property of the **picture** — one shot
 * of a cliff carries a dozen lines, and they have to read as one topo rather
 * than as a dozen people's handwriting. Saving it restamps the sector's routes,
 * so the whole cliff changes together.
 *
 * It fills the controls column whenever no route is open, which is also when
 * the photo behind it shows the sector's lines as they stand — the two are the
 * same view of the cliff, one to look at and one to set.
 */
export function SectorSettingsPanel({
  sector,
  settings,
  onSettingsChange,
  onSave,
  onImageUploaded,
  pendingImageUrl,
  saving,
  error,
}: SectorSettingsPanelProps) {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // The provider needs the API's own origin: a direct upload goes to Rails, not
  // to the host this panel is served from.
  const [origin, setOrigin] = useState(() => apiOrigin());

  useEffect(() => setOrigin(apiOrigin()), []);

  const preview = pendingImageUrl ?? sector.image_url;
  const corridorOn = settings.corridor > 0;

  return (
    <div className="space-y-4" data-testid="sector-settings">
      <div>
        <h2 className="text-sm font-semibold">{sector.name}</h2>
        <p className="text-xs text-muted-foreground">{t('cragAdmin.routes.sectorSettingsHint')}</p>
      </div>

      {/* The photo. A sector without one has nothing for its routes to be drawn
          on, so this is the way out of that rather than a trip to another page. */}
      <div className="space-y-2">
        <Label>{t('cragAdmin.routes.sectorPhoto')}</Label>
        <div className="flex items-center gap-3">
          {preview ? (
            <img src={preview} alt="" className="size-14 shrink-0 rounded border object-cover" />
          ) : (
            <span className="flex size-14 shrink-0 items-center justify-center rounded border bg-muted">
              <ImageIcon className="size-5 text-muted-foreground" aria-hidden />
            </span>
          )}
          <DirectUploadProvider
            origin={origin}
            headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
            onSuccess={(signedIds: string[]) => {
              setUploading(false);
              if (signedIds?.length) onImageUploaded(signedIds[0], '');
            }}
            render={({ handleUpload }: { handleUpload: (files: FileList) => void }) => {
              const select = (event: React.ChangeEvent<HTMLInputElement>) => {
                const files = event.target.files;
                if (!files?.length) return;
                setUploading(true);
                // Shown straight away: the upload is a round trip, and a sector
                // that looks unchanged until it lands reads as a failed press.
                onImageUploaded('', URL.createObjectURL(files[0]));
                handleUpload(files);
                event.target.value = '';
              };

              return (
                <div className="flex flex-wrap gap-2">
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={select} />
                  <input ref={galleryInputRef} type="file" accept="image/*" className="sr-only" onChange={select} />
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => cameraInputRef.current?.click()}>
                    {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Camera className="mr-1 size-4" aria-hidden />}
                    {t('zoneManager.camera')}
                  </Button>
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => galleryInputRef.current?.click()}>
                    <ImageIcon className="mr-1 size-4" aria-hidden />
                    {t('zoneManager.gallery')}
                  </Button>
                </div>
              );
            }}
          />
        </div>
      </div>

      {/* Which way this wall faces, and so when it is in the sun. It is the
          sector's rather than the crag's: on one cliff the sunny wall and the
          shaded one are twenty metres apart, and the climber reads it off the
          sector list on the crag's Info tab. */}
      <div className="space-y-2 border-t pt-3">
        <Label htmlFor="sector-exposure">{t('cragAdmin.routes.exposure')}</Label>
        <Select
          value={settings.exposure || NO_EXPOSURE}
          onValueChange={(next) =>
            onSettingsChange({ exposure: next === NO_EXPOSURE ? '' : (next as ZoneExposure) })
          }
        >
          <SelectTrigger id="sector-exposure">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_EXPOSURE}>{t('cragAdmin.routes.noExposure')}</SelectItem>
            {ZONE_EXPOSURES.map((exposure) => (
              <SelectItem key={exposure} value={exposure}>
                {t(`cragInfo.exposures.${exposure}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5 border-t pt-3">
        <div className="flex items-baseline justify-between gap-2">
          <Label htmlFor="sector-thickness" className="text-xs font-normal text-muted-foreground">
            {t('gymAdmin.scans.thickness')}
          </Label>
          <span className="text-xs tabular-nums text-muted-foreground">
            {(settings.thickness * 100).toFixed(1)}%
          </span>
        </div>
        <input
          id="sector-thickness"
          type="range"
          min={MIN_ROUTE_THICKNESS}
          max={MAX_ROUTE_THICKNESS}
          step={0.001}
          value={settings.thickness}
          onChange={(event) => onSettingsChange({ thickness: Number(event.target.value) })}
          className="w-full accent-primary"
        />
      </div>

      {/* Off by default outdoors: the lit band answers "which of these holds are
          mine" on a gym wall, and rock has no holds to tell apart. Still offered,
          because a crowded cliff is the one case where it earns its place. */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="sector-corridor-on" className="text-xs font-normal text-muted-foreground">
            {t('gymAdmin.scans.corridor')}
          </Label>
          <Switch
            id="sector-corridor-on"
            checked={corridorOn}
            onCheckedChange={(on: boolean) =>
              onSettingsChange({ corridor: on ? DEFAULT_ROUTE_CORRIDOR : 0 })
            }
          />
        </div>
        {corridorOn && (
          <>
            <input
              id="sector-corridor"
              type="range"
              aria-label={t('gymAdmin.scans.corridor')}
              min={MIN_ROUTE_CORRIDOR}
              max={MAX_ROUTE_CORRIDOR}
              step={0.01}
              value={settings.corridor}
              onChange={(event) => onSettingsChange({ corridor: Number(event.target.value) })}
              className="w-full accent-primary"
            />
            <p className="text-xs tabular-nums text-muted-foreground">
              {Math.round(settings.corridor * 100)}%
            </p>
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button type="button" size="sm" onClick={onSave} disabled={saving || uploading}>
        {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
        {t('cragAdmin.routes.saveSector')}
      </Button>
      <p className="text-xs text-muted-foreground">{t('cragAdmin.routes.restampHint')}</p>
    </div>
  );
}

/** Where direct uploads go — the API host, not the one serving this panel. */
function apiOrigin() {
  const fallback = {
    host: window.location.hostname,
    port: window.location.port || undefined,
    protocol: window.location.protocol.replace(':', ''),
  };
  const baseURL = apiClient.getBaseURL();
  if (!baseURL) return fallback;

  try {
    const url = new URL(baseURL);
    return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
  } catch {
    return fallback;
  }
}
